import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { MenuIcon } from '@/components/MenuIcon';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';
import {
  useMarket,
  type MarketFuel,
  type MarketFuelEfficiency,
  type MarketMaterial,
  type MarketRate,
} from '@/pages/home/useMarket';
import { WidgetFrame } from '@/pages/home/widgets/WidgetFrame';

/**
 * 환율 · 자재 단가.
 *
 * <h2>한 위젯에 성격이 다른 두 칸이 있다</h2>
 * 위는 <b>고시 환율</b>(한국수출입은행, 무료 API)이고 아래는 <b>회사가 정한 자재 단가</b>다.
 * 섞지 않고 선으로 가른 뒤 각자의 기준일을 붙이는 이유가 여기 있다 — 위 칸은 어제 값이라도
 * 맞는 값이지만, 아래 칸은 담당자가 넣은 날 이후로 시장이 얼마든지 움직였을 수 있다.
 *
 * <h2>철근 단가를 자동으로 받지 않는 이유</h2>
 * 시중 유통단가를 무료로 주는 곳이 없다. 물가정보(KPI)·건설물가는 유료 구독이고 그 화면을 긁는
 * 것은 약관 위반이며, 조달청 가격은 조달가라 현장이 사는 값과 다르다. 그래서 <b>자동인 척하지
 * 않고</b> 담당자가 넣은 값에 기준일과 출처를 붙여 보여 준다(자산관리 › 자재 단가 설정).
 *
 * <h2>넣지 않으면 나오지 않는다 (사용자 지시 2026-08-27)</h2>
 * 단가가 한 줄도 없으면 아래 칸은 <b>아예 그려지지 않는다.</b> 「미등록」 자리를 남겨 두면 그것이
 * 곧 담당자에게 매일 보이는 독촉이 되고, 이 기능은 그러라고 만든 것이 아니다. 환율만으로도
 * 위젯은 제 몫을 한다.
 *
 * <h2>고장을 홍보하지 않는다 (사용자 지시 2026-09-02)</h2>
 * 환율을 못 받아도 사유를 띄우지 않는다. 사유는 관리자 화면에서만 말한다(환경설정 › 외부 API 설정).
 */

/** 반칸(sm)에서도 접히지 않을 만큼만. 넘치는 줄은 설정 화면에서 순서를 올려 고른다. */
const VISIBLE_MATERIALS = 4;

export function MarketWidget() {
  const market = useMarket();
  const snapshot = market.data;
  const fuels = snapshot?.fuels ?? [];

  return (
    <WidgetFrame
      title="환율 · 자재 단가"
      icon="chart"
      to="/assets/material-prices"
      toLabel="단가 설정"
      pending={market.isPending}
      error={market.error}
      // 그릴 것이 하나도 없는 상태. 서버는 이제 여기서 오류를 내지 않으므로(MarketService) 남는
      // 경우는 「수집이 아직」 하나뿐이다 — 그 사실만 말하고 사유는 묻지 않는다.
      empty={!!snapshot && snapshot.rates.length === 0 && fuels.length === 0 && snapshot.materials.length === 0}
      emptyText="데이터를 가져오는 중입니다."
      // 우리 API 자체가 못 닿았을 때만 쓰인다(WidgetFrame 은 error 일 때만 이 버튼을 그린다).
      onRetry={market.refetch}
      headerRight={
        snapshot?.ratesQuotedOn && (
          <span className="flex-shrink-0 text-ui-xs" style={{ color: 'var(--text-muted)' }}>
            {monthDay(snapshot.ratesQuotedOn)} 고시
          </span>
        )
      }
    >
      {snapshot && (
        <>
          {/* 아직 안 채워졌으면 이 칸이 통째로 없다. 자재 단가와 같은 규칙 — 「없다」 를 말하지 않는다. */}
          {snapshot.rates.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {snapshot.rates.map((rate) => (
                <RateCell key={rate.currency} rate={rate} />
              ))}
            </div>
          )}

          {/* 유가(오피넷 전국 평균). 수집이 아직이면 이 칸이 통째로 없다 — 환율과 같은 규칙.
              환율과는 성격이 다른 묶음이라 자재 단가처럼 선으로 가른다. */}
          {fuels.length > 0 && (
            <div
              className={snapshot.rates.length > 0 ? 'mt-3 pt-2.5' : undefined}
              style={snapshot.rates.length > 0 ? { borderTop: '1px solid var(--border)' } : undefined}
            >
              {/* 머리글의 「고시」 는 환율 날짜다. 유가는 하루에도 몇 번 바뀌어 받아 온 시각을 따로 단다. */}
              <div className="flex items-baseline justify-between gap-2 text-ui-2xs" style={{ color: 'var(--text-muted)', marginBottom: '6px' }}>
                <span>유가 · 전국 평균 원/L</span>
                {(snapshot.fuelsMeasuredAt || snapshot.fuelsOn) && (
                  <span className="flex-shrink-0">
                    {snapshot.fuelsMeasuredAt
                      ? `${measuredAtText(snapshot.fuelsMeasuredAt)} 측정`
                      : `${monthDay(snapshot.fuelsOn!)} 기준`}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {fuels.map((fuel) => (
                  <FuelCell key={fuel.product} fuel={fuel} />
                ))}
              </div>
              {(snapshot.fuelEfficiencies?.length ?? 0) > 0 && (
                <FuelEfficiencyRow
                  items={snapshot.fuelEfficiencies!}
                  canEdit={!!snapshot.canEditFuelEfficiency}
                />
              )}
            </div>
          )}

          {/* 등록된 단가가 없으면 이 아래가 통째로 없다. 「미등록」 도 「등록해 주세요」 도 두지 않는다. */}
          {snapshot.materials.length > 0 && (
            <div
              className={snapshot.rates.length > 0 || fuels.length > 0 ? 'mt-3 pt-2.5' : undefined}
              // 가르는 선은 <b>가를 것이 둘일 때만</b> 긋는다. 위 칸이 비었는데 선을 두면 위에
              // 무언가가 있다가 사라진 것처럼 보인다.
              style={snapshot.rates.length > 0 || fuels.length > 0 ? { borderTop: '1px solid var(--border)' } : undefined}
            >
              <div className="space-y-0.5">
                {snapshot.materials.slice(0, VISIBLE_MATERIALS).map((material) => (
                  <MaterialRow key={`${material.name}|${material.spec ?? ''}`} material={material} />
                ))}
              </div>
              <p className="text-ui-2xs" style={{ color: 'var(--text-muted)', marginTop: '6px' }}>
                {sourceSummary(snapshot.materials)}
              </p>
            </div>
          )}
        </>
      )}
    </WidgetFrame>
  );
}

function RateCell({ rate }: { rate: MarketRate }) {
  return (
    <div className="px-2 py-1.5 rounded-lg" style={{ background: 'var(--bg-hover)' }}>
      <p className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
        {rate.label}
        {/* 엔화만 100 이다. 단위를 감추면 「9.4원」 이 되어 아무도 자기가 아는 환율과 견주지 못한다. */}
        {rate.unit !== 1 && <span> ({rate.unit})</span>}
      </p>
      <div className="flex items-baseline gap-1">
        <span className="text-ui-lg" style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
          {rate.rate.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
        <ChangeMark value={rate.change} digits={2} />
      </div>
      <Sparkline values={rate.trend} label={rate.label} />
    </div>
  );
}

function FuelCell({ fuel }: { fuel: MarketFuel }) {
  return (
    <div className="px-2 py-1.5 rounded-lg" style={{ background: 'var(--bg-hover)' }}>
      <p className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
        {fuel.label}
      </p>
      <div className="flex items-baseline gap-1 flex-wrap">
        <span className="text-ui-lg" style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
          {Math.round(fuel.price).toLocaleString('ko-KR')}
        </span>
        <ChangeMark value={fuel.change} digits={2} />
      </div>
      <Sparkline values={fuel.trend} label={fuel.label} />
    </div>
  );
}

/**
 * 차량별 연비 — 회사가 정한 기준값(서버 `FuelEfficiencyService`). 유가 칸과 같은 칸으로 그 연료 아래에 선다.
 *
 * 연필은 인사팀·최고관리자에게만 선다(사용자 지시 2026-10-28 「인사팀만 위젯에서 연필을 넣고」). 누르면
 * 그 자리의 숫자가 입력칸이 되고, 저장하면 서버가 한 번 더 권한을 본다.
 */
function FuelEfficiencyRow({ items, canEdit }: { items: MarketFuelEfficiency[]; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string> | null>(null);

  const save = useMutation({
    mutationFn: (values: Record<string, number>) =>
      apiFetch<void>('/v1/home/market/fuel-efficiency', { method: 'PUT', body: values }),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.market });
    },
  });

  const startEdit = () => {
    save.reset();
    setDraft(Object.fromEntries(items.map((item) => [item.product, String(item.kmPerL)])));
  };

  const submit = () => {
    if (!draft) return;
    save.mutate(Object.fromEntries(items.map((item) => [item.product, Number(draft[item.product])])));
  };

  return (
    <div className="mt-2">
      <div
        className="flex items-center justify-between gap-2 text-ui-2xs"
        style={{ color: 'var(--text-muted)', marginBottom: '6px', minHeight: '22px' }}
      >
        <span>차량별 연비</span>
        {canEdit &&
          (draft ? (
            <span className="flex items-center gap-1">
              <button type="button" onClick={() => setDraft(null)} className="btn-secondary btn-xs" disabled={save.isPending}>
                취소
              </button>
              <button type="button" onClick={submit} className="btn-primary btn-xs" disabled={save.isPending}>
                저장
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={startEdit}
              className="btn-ghost p-1"
              aria-label="차량별 연비 수정"
              title="차량별 연비 수정"
              style={{ color: 'var(--text-muted)' }}
            >
              <MenuIcon name="pencil" size={14} />
            </button>
          ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {items.map((item) => (
          <div key={item.product} className="px-2 py-1.5 rounded-lg" style={{ background: 'var(--bg-hover)' }}>
            <p className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
              {item.label} 연비
            </p>
            {draft ? (
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                min="0.1"
                className="input-field input-xs"
                value={draft[item.product] ?? ''}
                onChange={(event) => setDraft({ ...draft, [item.product]: event.target.value })}
                onKeyDown={(event) => event.key === 'Enter' && submit()}
                aria-label={`${item.label} 연비`}
              />
            ) : (
              <div className="flex items-baseline gap-1">
                <span className="text-ui-lg" style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                  {item.kmPerL}
                </span>
                <span className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>km/L</span>
              </div>
            )}
          </div>
        ))}
      </div>
      {save.error && (
        <p className="text-ui-2xs" style={{ color: 'var(--danger)', marginTop: '6px' }}>
          {save.error instanceof Error ? save.error.message : '저장하지 못했습니다.'}
        </p>
      )}
    </div>
  );
}

const SPARK_W = 100;
const SPARK_H = 20;
/** 위아래 여백. 선이 틀에 닿으면 잘린 것처럼 보인다. */
const SPARK_PAD = 2.5;
/** 끝점 지름의 절반. 좌우를 이만큼 물려야 마지막 점이 잘리지 않는다. */
const SPARK_DOT = 1.8;

/**
 * 통화 하나의 추이.
 *
 * <p>축도 눈금도 없다 — 값은 바로 위에 숫자로 적혀 있고 여기서 읽을 것은 모양뿐이다. 0 을 바닥으로
 * 잡으면 1,380원대의 ±5원이 수평선이 되므로 그 통화의 최저~최고 사이만 편다. 통화마다 눈금이
 * 달라 네 칸의 기울기를 서로 견줄 수 없고, 그래서 축을 그리지 않는다.
 *
 * <p>색을 쓰지 않는 이유는 같은 칸의 ▲▼ 가 이미 색으로 등락을 말하기 때문이다 — 선에 색을 입히면
 * 하필 「내렸다」 와 같은 색이 된다.
 */
function Sparkline({ values, label }: { values: number[]; label: string }) {
  // 점 하나는 추이가 아니다. 한 점을 이은 수평선은 「안 움직였다」 는 거짓말이 된다.
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const plotW = SPARK_W - SPARK_DOT * 2;
  const plotH = SPARK_H - SPARK_PAD * 2;
  const step = plotW / (values.length - 1);

  const points = values.map((value, index) => ({
    x: SPARK_DOT + index * step,
    // 값이 전부 같으면(span 0) 가운데 수평선. 나누기 0 을 피하는 자리이기도 하다.
    y: SPARK_PAD + (span === 0 ? plotH / 2 : (1 - (value - min) / span) * plotH),
  }));
  // `points` 는 `values` 와 길이가 같고 위에서 2개 미만을 걸렀다 — 타입만 그 사실을 모른다.
  const last = points[points.length - 1]!;

  const fmt = (value: number) => value.toLocaleString('ko-KR', { maximumFractionDigits: 2 });

  return (
    <svg
      viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
      role="img"
      // 그림을 못 보는 사람에게도 같은 말을 한다 — 몇 회 고시분의, 어디서 어디까지인가.
      aria-label={`${label} 최근 ${values.length}회 고시 추이 · ${fmt(min)} ~ ${fmt(max)}`}
      style={{ width: '100%', height: 'auto', display: 'block', marginTop: '2px' }}
    >
      {/* title 을 두지 않는다 — React 19 는 title 을 문서 메타데이터로 끌어올린다. 범위는 aria-label 이 말한다. */}
      <polyline
        points={points.map((point) => `${point.x},${point.y}`).join(' ')}
        fill="none"
        stroke="var(--text-muted)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        // 칸 너비에 따라 그림 전체가 확대·축소되는데, 선 굵기까지 같이 커지면 좁은 화면에서는
        // 실선이고 넓은 화면에서는 굵은 띠가 된다. 굵기만 화면 기준으로 못박는다.
        vectorEffect="non-scaling-stroke"
      />
      {/* 끝점만 찍는다 — 「지금」 이 어디인지. 나머지 점을 다 찍으면 선이 안 보인다. */}
      <circle cx={last.x} cy={last.y} r="1.8" fill="var(--text-secondary)" />
    </svg>
  );
}

function MaterialRow({ material }: { material: MarketMaterial }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="min-w-0 flex-1 truncate text-ui-md" style={{ color: 'var(--text-secondary)' }}>
        {material.name}
        {material.spec && (
          <span style={{ color: 'var(--text-muted)' }}> {material.spec}</span>
        )}
      </span>
      <span className="flex-shrink-0 flex items-baseline gap-1">
        <span className="text-ui-md" style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
          {Math.round(material.unitPrice).toLocaleString('ko-KR')}
        </span>
        <span className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>원/{material.unit}</span>
        <ChangeMark value={material.change} digits={0} />
      </span>
    </div>
  );
}

/**
 * ▲/▼ 와 차이. 값이 없으면 <b>아무것도 그리지 않는다</b> — 「0」 이나 「—」 를 두면 "안 움직였다" 로
 * 읽히는데, 실제로는 견줄 직전 값이 아직 없는 것이다.
 */
function ChangeMark({ value, digits }: { value: number | null; digits: number }) {
  if (value == null || value === 0) return null;
  const rising = value > 0;
  return (
    <span className="text-ui-2xs" style={{ color: rising ? 'var(--danger)' : 'var(--accent)' }}>
      {rising ? '▲' : '▼'}
      {Math.abs(value).toLocaleString('ko-KR', { minimumFractionDigits: digits, maximumFractionDigits: digits })}
    </span>
  );
}

/**
 * 아래 칸의 꼬리표. <b>기준일과 출처가 이 칸의 값 대부분</b>이다 — 자동 수집이 아니라 사람이 넣은
 * 숫자라, 언제 적힌 것인지 모르면 그 숫자를 믿을 수도 의심할 수도 없다.
 *
 * 줄마다 기준일이 다를 수 있어 <b>가장 오래된 것</b>을 쓴다. 가장 최근 것을 쓰면 한 줄만 갱신한
 * 표가 전부 새것처럼 보인다.
 */
function sourceSummary(materials: MarketMaterial[]): string {
  const oldest = materials.reduce(
    (acc, item) => (item.quotedOn < acc ? item.quotedOn : acc),
    materials[0]?.quotedOn ?? '',
  );
  const sources = Array.from(
    new Set(materials.map((item) => item.sourceNote).filter((value): value is string => !!value)),
  );
  // 출처가 여럿이면 적지 않는다. 좁은 칸에 둘을 욱여넣으면 어느 숫자가 어느 출처인지 되레 흐려진다.
  const source = sources.length === 1 ? ` · ${sources[0]}` : '';
  return `자재 단가 ${oldest} 기준${source}`;
}

/** ISO 시각 → `10.28 14:10`(이 브라우저 시간대). */
function measuredAtText(iso: string): string {
  const at = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(at.getMonth() + 1)}.${pad(at.getDate())} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/** `2026-08-27` → `08.27`. 머리글의 좁은 칸에 들어가야 한다. */
function monthDay(dateOnly: string): string {
  return dateOnly.slice(5).replace('-', '.');
}
